import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./HealthTech.css";
import Testimonial from "../../../Testimonial/Testimonial";

const HealthTech = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const [activeTab, setActiveTab] = useState(0);

  const solutionsData = [
    {
      title: "Telemedicine",
      description:
        "Our telemedicine platform enables secure virtual consultations between patients and healthcare providers from anywhere.",
      features: [
        "HD video conferencing with minimal latency",
        "Secure messaging and file sharing",
        "Appointment scheduling and reminders",
        "Integration with billing and EHR systems",
      ],
      visualType: "telemedicine",
    },
    {
      title: "Patient Portal",
      description:
        "Empower patients with secure access to their health information and communication tools with our patient portal solution.",
      features: [
        "Secure messaging with healthcare providers",
        "Appointment request and management",
        "Access to test results and medical history",
        "Online bill payment and insurance information",
      ],
      visualType: "portal",
    },
    {
      title: "Practice Management",
      description:
        "Streamline your healthcare practice operations with our comprehensive practice management software.",
      features: [
        "Appointment scheduling and calendar management",
        "Billing and claims processing",
        "Inventory and supply chain management",
        "Reporting and analytics dashboard",
      ],
      visualType: "management",
    },
    {
      title: "EHR Systems",
      description:
        "Our comprehensive EHR system streamlines patient data management, improves accuracy, and enhances coordination between healthcare providers.",
      features: [
        "Seamless integration with existing systems",
        "Customizable templates for different specialties",
        "Real-time access to patient records",
        "Interoperability with health information exchanges",
      ],
      visualType: "ehr",
    },
  ];

const featuresData = [
    {
      icon: "📊",
      title: "Real-time Analytics",
      description:
        "Monitor patient health data with advanced analytics and predictive insights.",
    },
    {
      icon: "🔒",
      title: "Secure Data Management",
      description:
        "HIPAA-compliant data storage and transmission with end-to-end encryption.",
    },
    {
      icon: "🤖",
      title: "AI-Powered Diagnostics",
      description:
        "Leverage machine learning for early detection and accurate diagnosis.",
    },
    {
      icon: "🌐",
      title: "Interoperability Solutions",
      description:
        "Seamless data exchange between different healthcare systems and providers.",
    },
    {
      icon: "📱",
      title: "Mobile Health Integration",
      description:
        "Connect with wearable devices and mobile health apps for continuous monitoring.",
    },
    {
      icon: "💬",
      title: "Patient Engagement Tools",
      description:
        "Interactive tools to improve patient communication and health outcomes.",
    },
    {
      icon: "⚡",
      title: "Automated Workflows",
      description:
        "Streamline clinical processes with intelligent automation and task management.",
    },
    {
      icon: "📈",
      title: "Population Health Management",
      description:
        "Analyze and improve health outcomes across patient populations.",
    },
    {
      icon: "🔍",
      title: "Advanced Reporting",
      description:
        "Customizable reports and dashboards for data-driven decision making.",
    },
    {
      icon: "🧠",
      title: "Clinical Decision Support",
      description:
        "AI-assisted tools that provide evidence-based recommendations during treatment planning.",
    },
    {
      icon: "🔄",
      title: "Blockchain Health Records",
      description:
        "Decentralized, tamper-proof patient records with transparent access tracking.",
    },
    {
      icon: "🏠",
      title: "Remote Patient Monitoring",
      description:
        "Continuous tracking of patient health metrics from the comfort of their homes.",
    },
    {
      icon: "💊",
      title: "Smart Medication Management",
      description:
        "Automated prescription tracking, reminders, and adherence monitoring.",
    },
    {
      icon: "🦠",
      title: "Predictive Outbreak Analytics",
      description:
        "Early detection of disease patterns and potential outbreaks using AI algorithms.",
    },
    {
      icon: "👁️",
      title: "Medical Imaging AI",
      description:
        "Advanced image recognition for radiology, pathology, and diagnostic imaging.",
    },
    {
      icon: "🧬",
      title: "Genomic Data Integration",
      description:
        "Personalized treatment plans based on genetic information and biomarkers.",
    },
    {
      icon: "📋",
      title: "Digital Twin Technology",
      description:
        "Virtual patient models for simulating treatments and predicting outcomes.",
    },
    {
      icon: "🗣️",
      title: "Voice-Enabled Clinical Documentation",
      description:
        "AI-powered speech recognition for hands-free patient charting and notes.",
    },
    {
      icon: "🔮",
      title: "Predictive Risk Stratification",
      description:
        "Identify high-risk patients and proactively manage their care plans.",
    },
    {
      icon: "🤝",
      title: "Collaborative Care Platforms",
      description:
        "Secure communication tools for care teams across multiple facilities.",
    },
    {
      icon: "🧪",
      title: "Lab Information System",
      description:
        "Streamlined management of laboratory tests, results, and workflows.",
    },
    {
      icon: "❤️",
      title: "Cardiac Care Monitoring",
      description:
        "Specialized tracking and analysis for cardiovascular health parameters.",
    },
    {
      icon: "👶",
      title: "Maternal & Infant Health Tracking",
      description:
        "Comprehensive monitoring solutions for obstetric and pediatric care.",
    },
    {
      icon: "🧘",
      title: "Mental Health & Wellness Tools",
      description:
        "Digital therapeutics and monitoring for behavioral health conditions.",
    },
    {
      icon: "🧭",
      title: "Patient Navigation System",
      description:
        "Guided care pathways and assistance for patients through treatment journeys.",
    },
    {
      icon: "⚕️",
      title: "Tele-ICU & Critical Care",
      description:
        "Remote monitoring and consultation for intensive care units.",
    },
    {
      icon: "🌡️",
      title: "Smart Vital Signs Monitoring",
      description:
        "Automated collection and analysis of patient vitals with alert systems.",
    },
    {
      icon: "🏥",
      title: "Virtual Hospital Platforms",
      description:
        "Complete digital hospital experiences with remote consultations and monitoring.",
    },
    {
      icon: "🔬",
      title: "Precision Medicine Solutions",
      description:
        "Tailored treatments based on individual patient characteristics and genetics.",
    },
    {
      icon: "📅",
      title: "Intelligent Scheduling Systems",
      description:
        "AI-optimized appointment booking that reduces wait times and improves efficiency.",
    },
    {
      icon: "💉",
      title: "Smart Injection Devices",
      description:
        "Connected auto-injectors that track dosage and administer medication precisely.",
    },
    {
      icon: "👁️‍🗨️",
      title: "Augmented Reality Surgery",
      description:
        "AR-guided surgical procedures for enhanced precision and visualization.",
    },
    {
      icon: "🧫",
      title: "Automated Microbiology Analysis",
      description:
        "AI-driven analysis of microbiological samples for faster diagnosis.",
    },
    {
      icon: "🚑",
      title: "Emergency Response Coordination",
      description:
        "Integrated systems for managing emergency care across multiple facilities.",
    },
    {
      icon: "📚",
      title: "Medical Education Platforms",
      description:
        "Virtual training and continuous education tools for healthcare professionals.",
    },
    {
      icon: "🌿",
      title: "Integrative Medicine Tools",
      description:
        "Platforms that combine conventional and complementary medicine approaches.",
    },
    {
      icon: "👵",
      title: "Aging-in-Place Technologies",
      description:
        "Solutions that support elderly patients to live independently at home.",
    },
    {
      icon: "🔋",
      title: "Wearable Energy Harvesting",
      description:
        "Self-powered medical devices that generate energy from body movement.",
    },
    {
      icon: "🧩",
      title: "Personalized Health Avatars",
      description:
        "Digital representations of patients that simulate health outcomes and treatments.",
    },
    {
      icon: "🌙",
      title: "Sleep Health Monitoring",
      description:
        "Advanced sleep tracking and disorder diagnosis using non-invasive sensors.",
    },
  ];

  return (
    <div>
      {/* ✅ SEO Meta Tags with Helmet */}
      <Helmet>
        <title>
          HealthTech Solutions | AI, Blockchain, Cloud, Web & Mobile Development
        </title>
        <meta
          name="description"
          content="Explore HealthTech solutions with cutting-edge tools and technology stack including AI, Blockchain, Cloud Computing, Web Development, Mobile Apps, IoT, Data Science, and more."
        />
        <meta
          name="keywords"
          content="HealthTech, AI development, Artificial Intelligence, Blockchain, Ethereum, Solidity, Hyperledger, Cloud Computing, Web Development, Mobile App Development, React Native, Angular, Node.js, PHP, Laravel, Django, Data Science, IoT, Virtual Reality, Augmented Reality, MySQL, MongoDB, PostgreSQL, Oracle, SaaS, Salesforce, Enterprise Solutions, Full Stack Development, Appian, Amazon DynamoDB"
        />
      </Helmet>

      <div className="mk-healthtech-wrapper">
        {/* Navigation */}
        <nav className="mk-healthtech-nav">
          <div className="mk-healthtech-logo">
            <span className="mk-logo-icon">⚕</span>
            <span className="mk-logo-text">MK eTech</span>
          </div>
          <ul className="mk-nav-items">
            <li>
              <a href="#home">Home</a>
            </li>
            <li>
              <a href="#features">Features</a>
            </li>
            <li>
              <a href="#solutions">Solutions</a>
            </li>
            <li>
              <a href="#about">About</a>
            </li>
            <li>
              <a href="#contact">Contact</a>
            </li>
          </ul>
          <button className="mk-demo-btn">Request Demo</button>
        </nav>

        {/* Hero Section */}
        <section
          id="home"
          className={`mk-hero-section ${isVisible ? "mk-visible" : ""}`}
        >
          <div className="mk-hero-content">
            <h1 className="mk-hero-title">
              Revolutionizing Healthcare with{" "}
              <span className="mk-text-gradient">Advanced Technology</span>
            </h1>
            <p className="mk-hero-description">
              MK eTech provides cutting-edge software solutions that empower
              healthcare providers, improve patient outcomes, and streamline
              medical operations.
            </p>
            <div className="mk-hero-buttons">
              <a href="/contact-us">
                <button className="mk-secondary-btn">Contat Us</button>
              </a>
            </div>
          </div>
          <div className="mk-hero-visual">
            <div className="mk-pulse-animation">
              <div className="mk-pulse-circle mk-pulse-1"></div>
              <div className="mk-pulse-circle mk-pulse-2"></div>
              <div className="mk-pulse-circle mk-pulse-3"></div>
              <div className="mk-main-icon">❤</div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="mk-features-section">
          <h2 className="mk-section-title">Our Innovative Features</h2>
          <p className="mk-section-subtitle">
            Discover how our cutting-edge technology transforms healthcare
            delivery
          </p>

          <div className="mk-features-grid">
            {featuresData.map((feature, index) => (
              <div
                key={index}
                className={`mk-feature-card ${
                  activeFeature === index ? "mk-feature-active" : ""
                }`}
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="mk-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mk-feature-indicators">
            {featuresData.map((_, index) => (
              <button
                key={index}
                className={`mk-feature-indicator ${
                  activeFeature === index ? "mk-indicator-active" : ""
                }`}
                onClick={() => setActiveFeature(index)}
                aria-label={`View feature ${index + 1}`}
              />
            ))}
          </div>
        </section>

        {/* Solutions Section */}
        <section id="solutions" className="mk-solutions-section">
          <h2 className="mk-section-title">Healthcare Solutions</h2>
          <div className="mk-solutions-container">
            <div className="mk-solution-tabs">
              {solutionsData.map((solution, index) => (
                <button
                  key={index}
                  className={`mk-tab-btn ${
                    activeTab === index ? "mk-tab-active" : ""
                  }`}
                  onClick={() => setActiveTab(index)}
                >
                  {solution.title}
                </button>
              ))}
            </div>

            <div className="mk-solution-content">
              <div className="mk-solution-visual">
                <div
                  className={`mk-dashboard-animation mk-visual-${solutionsData[activeTab].visualType}`}
                >
                  {/* EHR Visual */}
                  {solutionsData[activeTab].visualType === "ehr" && (
                    <>
                      <div className="mk-dashboard-line mk-dashboard-line-1"></div>
                      <div className="mk-dashboard-line mk-dashboard-line-2"></div>
                      <div className="mk-dashboard-line mk-dashboard-line-3"></div>
                      <div className="mk-dashboard-card mk-dashboard-card-1"></div>
                      <div className="mk-dashboard-card mk-dashboard-card-2"></div>
                      <div className="mk-dashboard-card mk-dashboard-card-3"></div>
                    </>
                  )}

                  {/* Telemedicine Visual */}
                  {solutionsData[activeTab].visualType === "telemedicine" && (
                    <>
                      <div className="mk-telemedicine-circle mk-tc-1">
                        <div className="mk-tc-inner"></div>
                      </div>
                      <div className="mk-telemedicine-circle mk-tc-2">
                        <div className="mk-tc-inner"></div>
                      </div>
                      <div className="mk-telemedicine-line"></div>
                    </>
                  )}

                  {/* Patient Portal Visual */}
                  {solutionsData[activeTab].visualType === "portal" && (
                    <>
                      <div className="mk-portal-device mk-portal-mobile"></div>
                      <div className="mk-portal-device mk-portal-tablet"></div>
                      <div className="mk-portal-device mk-portal-desktop"></div>
                      <div className="mk-portal-connection"></div>
                    </>
                  )}

                  {/* Practice Management Visual */}
                  {solutionsData[activeTab].visualType === "management" && (
                    <>
                      <div className="mk-management-chart">
                        <div className="mk-chart-bar mk-chart-bar-1"></div>
                        <div className="mk-chart-bar mk-chart-bar-2"></div>
                        <div className="mk-chart-bar mk-chart-bar-3"></div>
                        <div className="mk-chart-bar mk-chart-bar-4"></div>
                      </div>
                      <div className="mk-management-card mk-mc-1"></div>
                      <div className="mk-management-card mk-mc-2"></div>
                      <div className="mk-management-card mk-mc-3"></div>
                    </>
                  )}
                </div>
              </div>

              <div className="mk-solution-text">
                <h3>{solutionsData[activeTab].title}</h3>
                <p>{solutionsData[activeTab].description}</p>
                <ul className="mk-solution-list">
                  {solutionsData[activeTab].features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mk-cta-section">
          <div className="mk-cta-content">
            <h2>Ready to Transform Your Healthcare Practice?</h2>
            <p>
              Join hundreds of healthcare providers who trust MK eTech with
              their digital transformation.
            </p>
            <a
              href="https://wa.me/919155599968?text=Hi!%20I%20am%20ready%20to%20transform%20my%20healthcare%20business%20with%20your%20digital%20solutions.%20Please%20connect%20me%20with%20your%20experts%20to%20discuss%20the%20next%20steps."
              target="_blank"
              rel="noreferrer"
            >
              <button className="mk-primary-btn mk-cta-btn">
                Get Started Today
              </button>
            </a>
          </div>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthTech;
